import React, { forwardRef, useMemo } from 'react';
import { Icon } from '../design/Icon';
import { fmt } from '../design/format';
import { Chip } from '../components/shared/Badges';
import { HierarchyTreePicker } from '../components/shared/HierarchyTreePicker';
import { MenuItem, MenuTitle, Popover, usePopover } from '../components/shared/Overlay';
import { useHierarchyFilter } from '../hooks/useHierarchyFilter';
import { DATE_PRESETS, resolvePreset } from '../hooks/dateRanges';
import { useAccountTimeZone } from './ShellStatus';

function DivisionChip() {
  const h = useHierarchyFilter();
  const pop = usePopover();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={260}
      label={h.topLevelName}
      anchor={
        <Chip icon="sitemap" keyLabel={h.topLevelName} active={Boolean(h.filter.nodeId)} aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <span>{h.selectedNode ? h.selectedNode.name : 'All'}</span>
          <Icon name="chevD" className="ico-sm" />
        </Chip>
      }
    >
      <MenuTitle>{h.topLevelName}</MenuTitle>
      <MenuItem
        checkable
        on={!h.filter.nodeId}
        onClick={() => {
          h.setDivision(null);
          pop.close();
        }}
      >
        All {h.topLevelPlural.toLowerCase()}
      </MenuItem>
      {h.topNodes.map((node) => (
        <MenuItem
          key={node.id}
          checkable
          on={h.filter.nodeId === node.id}
          onClick={() => {
            h.setDivision(node.id);
            pop.close();
          }}
        >
          <span>{node.name}</span>
          <span className="caption" style={{ marginLeft: 'auto' }}>
            {h.terms.count(node.allCommunityIds.length)}
          </span>
        </MenuItem>
      ))}
      {h.status === 'error' && (
        <div className="caption" style={{ padding: '6px 9px' }}>
          {h.topLevelPlural} couldn't load.{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={h.reload}>
            Retry
          </button>
        </div>
      )}
    </Popover>
  );
}

function CommunityChip() {
  const h = useHierarchyFilter();
  const pop = usePopover();

  const groups = useMemo(() => {
    const nodes = h.selectedNode ? h.topNodes.filter((n) => n.id === h.selectedNode.id) : h.topNodes;
    const grouped = nodes.map((node) => ({
      id: node.id,
      name: node.name,
      items: node.allCommunityIds
        .map((id) => h.communitiesById[id])
        .filter(Boolean)
        .map((c) => ({ id: c.id, name: c.name, aliases: c.aliases, meta: c.units ? `${fmt.int(c.units)} units` : undefined })),
    }));
    if (!h.selectedNode) {
      const placed = new Set(grouped.flatMap((g) => g.items.map((i) => i.id)));
      const unplaced = h.communities.filter((c) => c.isActive !== false && !placed.has(c.id));
      if (unplaced.length) grouped.push({ id: 'unassigned', name: `No ${h.topLevelName.toLowerCase()}`, items: unplaced.map((c) => ({ id: c.id, name: c.name, aliases: c.aliases })) });
    }
    return grouped;
  }, [h.selectedNode, h.topNodes, h.communitiesById, h.communities, h.topLevelName]);

  const ids = h.filter.communityIds;
  const text = ids.length === 0 ? 'All' : ids.length === 1 ? h.communitiesById[ids[0]]?.name || '1 selected' : `${ids.length} selected`;

  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={340}
      label={h.communityLabel}
      anchor={
        <Chip icon="home" keyLabel={h.communityLabel} active={ids.length > 0} aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <span>{text}</span>
          <Icon name="chevD" className="ico-sm" />
        </Chip>
      }
    >
      <HierarchyTreePicker
        groups={groups}
        selected={ids}
        searchPlaceholder={`Search ${h.terms.communitiesLower} or aliases`}
        emptyText={`No ${h.terms.communityLower} matches. Search also checks aliases.`}
        onApply={(picked) => {
          h.setCommunities(picked);
          pop.close();
        }}
      />
    </Popover>
  );
}

function DateChip() {
  const h = useHierarchyFilter();
  const { zone } = useAccountTimeZone();
  const pop = usePopover();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={300}
      label="Date range"
      anchor={
        <Chip icon="calendar" keyLabel="Dates" aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <span>{h.range.rangeLabel}</span>
          <Icon name="chevD" className="ico-sm" />
        </Chip>
      }
    >
      <MenuTitle>Date range</MenuTitle>
      {DATE_PRESETS.map((preset) => (
        <MenuItem
          key={preset.id}
          checkable
          on={h.range.id === preset.id}
          onClick={() => {
            h.setPreset(preset.id);
            pop.close();
          }}
        >
          <span>{preset.label}</span>
          <span className="caption" style={{ marginLeft: 'auto' }}>
            {resolvePreset(preset.id, new Date(), zone).rangeLabel}
          </span>
        </MenuItem>
      ))}
    </Popover>
  );
}

/** Filter row under the top bar. Pages add their own filters into the slot through <Page filters={...}>. */
export const FilterBar = forwardRef(function FilterBar(props, slotRef) {
  const h = useHierarchyFilter();
  const { label } = useAccountTimeZone();
  return (
    <div className="filterbar" id="vl-filterbar" role="region" aria-label="Filters">
      <span className="row" style={{ gap: 6 }}>
        <DivisionChip />
        <Icon name="chevR" className="ico-sm muted" />
        <CommunityChip />
        {(h.filter.nodeId || h.filter.communityIds.length > 0) && (
          <button className="btn btn-ghost btn-sm" type="button" onClick={h.clear}>
            Clear
          </button>
        )}
      </span>
      <span className="sep" aria-hidden="true" />
      <DateChip />
      <span ref={slotRef} className="row-wrap" />
      <span className="applied">Times in {label}</span>
    </div>
  );
});
